import { useMemo } from 'react';
import type { Machine } from '@bridgewerk/shared';
import { Badge } from '../../../components/ui/Badge.js';
import type { BadgeTone } from '../../../components/ui/Badge.js';

type Severity = 'CRITICAL' | 'WARNING' | 'INFO' | 'OPPORTUNITY';

interface Insight {
  readonly id: string;
  readonly severity: Severity;
  readonly machineId?: string;
  readonly description: string;
  readonly action?: string;
  readonly timestamp: string;
}

const TONE: Record<Severity, BadgeTone> = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  INFO: 'info',
  OPPORTUNITY: 'opportunity',
};

const SEVERITY_BORDER: Record<Severity, string> = {
  CRITICAL: 'var(--color-red)',
  WARNING: 'var(--color-orange)',
  INFO: 'var(--color-accent)',
  OPPORTUNITY: 'var(--color-green)',
};

export interface IntelligencePanelProps {
  readonly machines: Machine[] | undefined;
}

export function IntelligencePanel({ machines }: IntelligencePanelProps): JSX.Element {
  const insights = useMemo(() => deriveInsights(machines ?? []), [machines]);
  const findings = insights.length;
  const updated = '2s ago';

  return (
    <section className="rounded-md border border-border bg-card overflow-hidden flex flex-col h-full">
      <header className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
            WerkOS Intelligence
          </h2>
          <span className="text-[10px] font-mono text-accent">WERK·OS</span>
        </div>
        <p className="mt-1 text-[11px] text-text-dim">
          {findings} {findings === 1 ? 'finding' : 'findings'}
          <span className="mx-1.5 text-muted">·</span>
          updated {updated}
        </p>
      </header>

      <div className="p-3 space-y-2 flex-1 min-h-[160px]">
        {findings === 0 ? <EmptyState /> : insights.slice(0, 4).map((i) => <InsightCard key={i.id} insight={i} />)}
      </div>

      <VoiceInput />
    </section>
  );
}

function InsightCard({ insight }: { readonly insight: Insight }): JSX.Element {
  return (
    <div
      className="rounded-md p-3 space-y-1"
      style={{
        backgroundColor: 'var(--color-card-2)',
        borderLeft: `3px solid ${SEVERITY_BORDER[insight.severity]}`,
      }}
    >
      <div className="flex items-center gap-2">
        <Badge tone={TONE[insight.severity]}>{insight.severity}</Badge>
        <span className="text-[10px] font-mono text-muted">{insight.timestamp}</span>
        {insight.machineId && (
          <span className="text-[10px] font-mono text-text-dim">{insight.machineId}</span>
        )}
      </div>
      <p className="text-[13px] leading-snug">{insight.description}</p>
      {insight.action && (
        <button
          type="button"
          className="text-[11px] font-mono text-accent hover:underline"
        >
          {insight.action} →
        </button>
      )}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-8">
      <p className="text-sm text-text-dim mb-1">WerkOS is learning your floor.</p>
      <p className="text-[11px] text-muted">Insights appear after the first 7 days.</p>
    </div>
  );
}

function VoiceInput(): JSX.Element {
  return (
    <div className="border-t border-border p-3" aria-label="WerkOS voice input">
      <div className="flex items-center gap-2 rounded-md bg-card-2 border border-border px-3 py-2 opacity-70">
        <Waveform />
        <input
          type="text"
          placeholder="Ask WerkOS anything..."
          disabled
          title="Coming soon"
          className="flex-1 bg-transparent text-sm placeholder:text-muted outline-none disabled:cursor-not-allowed"
        />
        <button
          type="button"
          disabled
          title="Coming soon"
          aria-label="Microphone (coming soon)"
          className="h-7 w-7 rounded-md border border-border flex items-center justify-center text-text-dim disabled:cursor-not-allowed"
        >
          ◉
        </button>
      </div>
    </div>
  );
}

function Waveform(): JSX.Element {
  return (
    <div className="flex items-end gap-[2px] h-4" aria-hidden>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <span
          key={i}
          className="block w-[2px] rounded-sm"
          style={{
            height: `${30 + ((i * 17) % 70)}%`,
            backgroundColor: 'var(--color-accent)',
            opacity: 0.5,
            animation: `pulse-glow 1.6s ease-in-out ${i * 80}ms infinite`,
          }}
        />
      ))}
    </div>
  );
}

function deriveInsights(machines: Machine[]): Insight[] {
  const now = 'just now';
  const out: Insight[] = [];
  for (const m of machines) {
    if (m.status === 'alarm' && m.alarm_code) {
      out.push({
        id: `alarm-${m.machine_id}`,
        severity: 'CRITICAL',
        machineId: m.machine_id,
        description: `Alarm ${m.alarm_code} active. Production halted.`,
        action: 'View machine',
        timestamp: now,
      });
    } else if (m.oee_percent < 50 && m.status !== 'offline') {
      out.push({
        id: `oee-${m.machine_id}`,
        severity: 'WARNING',
        machineId: m.machine_id,
        description: `OEE ${m.oee_percent.toFixed(0)}% — below the 50% floor for the last hour.`,
        action: 'Investigate',
        timestamp: now,
      });
    } else if (m.status === 'idle') {
      out.push({
        id: `idle-${m.machine_id}`,
        severity: 'INFO',
        machineId: m.machine_id,
        description: `Idle since last cycle. Schedule the next job to keep utilization up.`,
        action: 'Open schedule',
        timestamp: now,
      });
    }
  }
  return out;
}
